import { Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { VesTransactionsService } from '../../../services/ves-transactions.service';
import Swal from 'sweetalert2';

export interface RegistrarReferenciaOperadorModalData {
  /**
   * Si se pasa, el modal queda "locked" en ese rail: oculta el selector y
   * fija el valor. Usado en las vistas /admin/n58 y /admin/banco-plaza donde
   * el banco ya está implícito por la URL. En /admin/referencias-ves-admin
   * NO se pasa para que el operador pueda elegir.
   */
  lockedRail?: 'N58' | 'BANCO_PLAZA' | 'R4';
}

@Component({
  selector: 'app-registrar-referencia-operador-modal',
  templateUrl: './registrar-referencia-operador-modal.component.html',
})
export class RegistrarReferenciaOperadorModalComponent {
  form: FormGroup;
  loading = false;
  readonly lockedRail: 'N58' | 'BANCO_PLAZA' | 'R4' | null;

  constructor(
    private vesTransactionsService: VesTransactionsService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<RegistrarReferenciaOperadorModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) data?: RegistrarReferenciaOperadorModalData,
  ) {
    this.lockedRail = data?.lockedRail ?? null;
    this.form = this.fb.group({
      rail: new FormControl<'N58' | 'BANCO_PLAZA' | 'R4'>(
        this.lockedRail ?? 'N58',
        [Validators.required],
      ),
      referencia: new FormControl('', [Validators.required, Validators.minLength(6)]),
      monto: new FormControl(null, [Validators.required, Validators.min(0.01)]),
      precioCompra: new FormControl(null, [Validators.required, Validators.min(0.0001)]),
      motivo: new FormControl(''),
    });

    if (this.lockedRail) {
      this.form.get('rail')?.disable();
    }
  }

  railLabel(rail: 'N58' | 'BANCO_PLAZA' | 'R4'): string {
    if (rail === 'BANCO_PLAZA') return 'Banco Plaza';
    if (rail === 'R4') return 'R4 Conecta';
    return 'N58';
  }

  formatVES(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  registrar(): void {
    if (this.form.invalid || this.loading) return;
    this.loading = true;

    const rail = (this.lockedRail || this.form.get('rail')?.value || 'N58') as 'N58' | 'BANCO_PLAZA' | 'R4';
    const payload = {
      rail,
      referencia: String(this.form.get('referencia')?.value || '').trim(),
      monto: Number(this.form.get('monto')?.value),
      precioCompra: Number(this.form.get('precioCompra')?.value),
      motivo: (this.form.get('motivo')?.value || '').trim() || undefined,
    };

    this.vesTransactionsService.registrarReferenciaOperador(payload).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        const matchedClient = !!data?.userId && data?.tipoCuenta !== 'Interna';
        const railLabel = this.railLabel(rail);

        Swal.fire({
          icon: 'success',
          title: matchedClient ? 'Referencia asignada al cliente' : 'Referencia registrada',
          html: matchedClient
            ? `<p>La referencia coincidió con una carga PENDING del cliente y fue aprobada automáticamente.</p>
               <p style="margin-top: 8px; font-size: 0.85rem; color: #6b7280;">
                 Banco: <strong>${railLabel}</strong><br>
                 Usuario: <strong>#${data?.userId}</strong><br>
                 Monto: <strong>${this.formatVES(payload.monto)}</strong>
               </p>`
            : `<p>La referencia quedó registrada por operador en la cuenta interna <strong>${railLabel}</strong>.</p>
               <p style="margin-top: 8px; font-size: 0.85rem; color: #6b7280;">
                 Ningún cliente podrá usar esta referencia para cargar saldo en este banco.
               </p>`,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#1e5a3f',
        });
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.loading = false;
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err?.error?.message || err?.message || 'Error al registrar la referencia',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#1e5a3f',
        });
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
